import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import { MarketStatus, type Market } from '@nucleus-commerce/api-client/admin';
import { money } from '@nucleus-commerce/core';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyMarketForm, fromMarket, toMarketCreate, toMarketUpdate } from '../api/mappers';
import { useCreateMarket, useUpdateMarket } from '../hooks/use-market-mutations';
import { countryName } from '../schemas/countries';
import {
  marketFormSchema,
  ROUNDING_MODES,
  type MarketFormInput,
  type MarketFormOutput,
} from '../schemas/market-form-schema';
import { marketErrorTarget, takenCountries } from '../schemas/market-rules';

import { CountrySelect } from './country-select';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['name', 'countries', 'currency', 'status']);

export interface MarketDialogProps {
  storeId: string;
  /** The store currency, proposed for a new market. */
  storeCurrency: string;
  markets: readonly Market[];
  /** null creates; a market edits it. */
  market: Market | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Create or edit a market: name, countries, currency, rounding and status. */
export function MarketDialog({
  storeId,
  storeCurrency,
  markets,
  market,
  open,
  onOpenChange,
}: MarketDialogProps) {
  const { t } = useTranslation(['markets', 'common']);
  const create = useCreateMarket(storeId);
  const update = useUpdateMarket(storeId);
  const message = useErrorMessage();
  const form = useForm<MarketFormInput, unknown, MarketFormOutput>({
    resolver: useI18nResolver(marketFormSchema),
    defaultValues: market ? fromMarket(market) : emptyMarketForm(storeCurrency),
  });
  const mode = form.watch('rounding.mode');
  const currency = form.watch('currency') || storeCurrency;
  const isPending = create.isPending || update.isPending;
  const taken = takenCountries(markets, market?.id);

  const close = () => {
    onOpenChange(false);
    create.reset();
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      if (market) await update.mutateAsync({ id: market.id, body: toMarketUpdate(values) });
      else await create.mutateAsync(toMarketCreate(values));
      close();
    } catch (error) {
      const target = marketErrorTarget(error);
      // A missing rate gets its own words even though the backend also names the field.
      const rateMissing = target?.key === 'markets:errors.rateMissing';
      if (!rateMissing && applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) {
        return;
      }
      if (target) {
        const values = target.values;
        const countries = Array.isArray(values?.countries)
          ? values.countries.map((c) => countryName(String(c))).join(', ')
          : undefined;
        form.setError(target.field, {
          type: 'server',
          message: t(target.key, { ...values, countries }),
        });
      } else {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{market ? t('markets:edit.title') : t('markets:create.title')}</DialogTitle>
          <DialogDescription>{t('markets:create.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('markets:fields.name')}</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="countries"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('markets:fields.countries')}</FormLabel>
                  <FormControl>
                    <CountrySelect
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      taken={taken}
                      aria-invalid={fieldState.invalid}
                    />
                  </FormControl>
                  <FormDescription>{t('markets:fields.countriesHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('markets:fields.currency')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="uppercase"
                        maxLength={3}
                        onChange={(e) => {
                          const next = e.target.value.toUpperCase();
                          field.onChange(next);
                          const to = form.getValues('rounding.to');
                          if (to && /^[A-Z]{3}$/.test(next)) {
                            form.setValue('rounding.to', money(to.amount, next));
                          }
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      {currency !== storeCurrency && /^[A-Z]{3}$/.test(currency)
                        ? t('markets:fields.currencyConverts', {
                            from: storeCurrency,
                            to: currency,
                          })
                        : t('markets:fields.currencyHint')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('markets:fields.status')}</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={market?.isDefault ?? false}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(MarketStatus).map((status) => (
                          <SelectItem key={status} value={status}>
                            {t(`markets:status.${status}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {market?.isDefault ? (
                      <FormDescription>{t('markets:fields.defaultAlwaysActive')}</FormDescription>
                    ) : null}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="rounding.mode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('markets:fields.roundingMode')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ROUNDING_MODES.map((m) => (
                          <SelectItem key={m} value={m}>
                            {t(`markets:rounding.mode.${m}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {mode === 'ending' ? (
                <FormField
                  control={form.control}
                  name="rounding.ending"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('markets:fields.ending')}</FormLabel>
                      <FormControl>
                        <Input inputMode="numeric" maxLength={2} placeholder="99" {...field} />
                      </FormControl>
                      <FormDescription>{t('markets:fields.endingHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <FormField
                  control={form.control}
                  name="rounding.to"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('markets:fields.roundTo')}</FormLabel>
                      <FormControl>
                        <MoneyInput
                          currency={currency}
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <FormDescription>{t('markets:fields.roundToHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? t('common:actions.saving')
                  : market
                    ? t('common:actions.save')
                    : t('markets:create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
